import 'rxjs/add/operator/map';
import 'rxjs/add/operator/do';
import { fromPromise } from 'rxjs/observable/fromPromise';
import fetchJson$ from '../../common/fetch/as-json-stream';
import FetchError from '../../common/fetch/errors';
import {
	type ConfluencePage,
	type ConfluenceSearchResultWithLinks,
	type ConfluenceSpaceExpanded,
} from '../confluence/types';
import { type AccessRequestCapabilityResponse } from '../confluence/access-request-capabilities/types';
import { type LicenceInformation } from '../context/types';
import {
	PROJECT_PAGES_SOURCE_COMPONENT,
	PROJECT_PAGES_SOURCE_CONTEXT,
} from '../../common/constants';

const XFLOW_SERVICE_URL = '/gateway/api/xflow';
const BXP_SIGNUP_URL = '/gateway/api/bxp/signup/cloud';

const IGNORE_MAU_HEADER = 'x-atlassian-mau-ignore';
const EP_ENITITY_ID_HEADER = 'X-Atl-EmbeddablePage-EntityIDs';

export const CONFLUENCE_CREATE_SPACE_URL = '/wiki/rest/api/space';
export const CONFLUENCE_QUERY_SPACES_URL =
	'/wiki/rest/create-dialog/1.0/spaces?promotedSpacesLimit=10&otherSpacesLimit=1000';
export const ONBOARDING_SPACE_URL = '/wiki/rest/onboarding/1/store/global';
export const CONFLUENCE_SPACE_SEARCH_URL = `/gateway/api/xpsearch-aggregator/quicksearch/v1`;

export const getBlueprintsUrl = (spaceKey: string) =>
	`/wiki/rest/create-dialog/1.0/blueprints/web-items?spaceKey=${spaceKey}&_=${Date.now()}`;

export const getConfluenceCurrentUserUrl = () => `/wiki/rest/api/user/current?expand=operations`;

export const getLicenseInformationUrl = (cloudId: string) =>
	`${BXP_SIGNUP_URL}/${encodeURIComponent(cloudId)}/license-information`;

export const BASE_XFLOW_PROVISION_URL = `${XFLOW_SERVICE_URL}/provision/cloud`;
export const confluenceActivationBaseUrl = (cloudId: string) =>
	`${BASE_XFLOW_PROVISION_URL}/${encodeURIComponent(cloudId)}/product/confluence.ondemand/job/project-pages`;

export const getAggBaseUrl = () => '/gateway/api/graphql';

export const getSpaceContentUrl = (spaceKey: string) =>
	`/wiki/rest/api/space/${spaceKey}/content?depth=root`;

export const getSpaceAvailableUrl = (key: string) => `/wiki/ajax/spaceavailable.action?key=${key}`;

export const getConfluencePageSearchURL = (cqlQuery: string) =>
	`/wiki/rest/api/content/search?${new URLSearchParams({
		cql: cqlQuery,
		expand: 'space,link',
	}).toString()}`;

export const getConfluencePageUrl = (pageId: string) => `/wiki/rest/api/content/${pageId}`;

export const getConfluenceAccessCapabilitiesUrl = (cloudId: string) => {
	const params = new URLSearchParams({
		resource: `ari:cloud:confluence::site/${cloudId}`,
	});

	return `/gateway/api/invitations/v1/access-requests/capabilities?${params.toString()}`;
};

export const getPersonalizationSiteTraitsUrl = (cloudId?: string) =>
	`/gateway/api/tap-delivery/api/v3/personalization/site/${cloudId}`;

export const getJiraProjectPermissionsUrl = (projectKey: string) => {
	const params = new URLSearchParams({
		projectKey,
		permissions: `${JiraProjectPermissionType.CreateIssues},${JiraProjectPermissionType.EditIssues}`,
	});

	return `/rest/api/2/mypermissions?${params.toString()}`;
};

/**
 * Returns the license state of the specified product on the current instance.
 * Caveat: the requesting user has to be logged in, otherwise the request won't be authenticated.
 *
 * @param cloudId the identifier for the current instance
 * @returns LicenceInformation
 */
export const requestProductLicenceState$ = (cloudId: string) =>
	fetchJson$<LicenceInformation>(getLicenseInformationUrl(cloudId), {
		credentials: 'include',
	});

export const requestConfluenceSpaces$ = (projectId?: number, projectType?: string) =>
	fetchJson$(CONFLUENCE_QUERY_SPACES_URL, {
		method: 'GET',
		credentials: 'include',
		headers: {
			'Content-Type': 'application/json',
			[IGNORE_MAU_HEADER]: 'true',
			[EP_ENITITY_ID_HEADER]: JSON.stringify({ projectId, projectType }),
		},
	}).map((response) => [
		...new Set([...response.promotedSpaces.spaces, ...response.otherSpaces.spaces]),
	]);

/**
 * Attempts to create a confluence space given a space name
 * @param confluenceSpaceName name of the space being created
 * @param confluenceSpaceKey unique space key on Confluence
 * @param projectKey project key of the Jira project creating the confluence space
 *
 * Returns the newly created space's id
 */
export const requestCreateConfluenceSpace$ = (
	confluenceSpaceName: string,
	confluenceSpaceKey: string,
	projectKey: string,
	projectId?: number,
	projectType?: string,
) =>
	fetchJson$(CONFLUENCE_CREATE_SPACE_URL, {
		method: 'POST',
		credentials: 'include',
		headers: {
			'Content-Type': 'application/json',
			[IGNORE_MAU_HEADER]: 'true',
			[EP_ENITITY_ID_HEADER]: JSON.stringify({ projectId, projectType }),
		},
		body: JSON.stringify({
			key: confluenceSpaceKey,
			name: confluenceSpaceName,
			description: {
				plain: {
					value: projectKey ? `Confluence Space created for Jira Project ${projectKey}` : '',
					representation: 'plain',
				},
			},
		}),
	}).map((response) => ({
		spaceKey: response.key,
		spaceId: response.id,
		spaceUrl: `${response._links?.base}${response._links?.webui}`,
		projectKey,
	}));

/**
 * Returns whether a space with the provided key already exists or not.
 *
 * @param key - the space key to check the availability of
 *
 * Returns a boolean
 */
export const checkConfluenceSpaceAvailability$ = (
	key: string,
	projectId?: number,
	projectType?: string,
) =>
	fetchJson$(getSpaceAvailableUrl(key), {
		credentials: 'include',
		headers: {
			[IGNORE_MAU_HEADER]: 'true',
			[EP_ENITITY_ID_HEADER]: JSON.stringify({ projectId, projectType }),
		},
	}).map((response) => ({ available: response.available, key }));

/**
 * Retrieves the root page id from a space
 * @param spaceKey
 */
export const retrieveRootPageId$ = (spaceKey: string, projectId?: number, projectType?: string) =>
	fetchJson$(getSpaceContentUrl(spaceKey), {
		credentials: 'include',
		headers: {
			[IGNORE_MAU_HEADER]: 'true',
			[EP_ENITITY_ID_HEADER]: JSON.stringify({ projectId, projectType }),
		},
	}).map((response) => {
		const firstRootPageFallback = response.page.results[0].id;
		const rootPageWithSlashOverview = response.page.results
			.filter(
				(
					result: any, // dictated by the shape of the response from confluence
				) =>
					// find the page at the root level that is the space home
					result._links.webui.indexOf('/overview') >= 0,
			)
			.map((rootPage: { id: any }) => rootPage.id);
		return rootPageWithSlashOverview.length ? rootPageWithSlashOverview[0] : firstRootPageFallback;
	});

export const putFirstSpaceCreatedFlag$ = (projectId?: number, projectType?: string) =>
	fromPromise(
		fetch(ONBOARDING_SPACE_URL, {
			method: 'PUT',
			credentials: 'include',
			headers: {
				'Content-Type': 'application/json',
				[IGNORE_MAU_HEADER]: 'true',
				[EP_ENITITY_ID_HEADER]: JSON.stringify({ projectId, projectType }),
			},
			body: JSON.stringify({
				key: 'is-first-space-created',
				value: 'true',
			}),
		}).then((response) => {
			if (!response.ok) {
				const { status } = response;
				return response.text().then((str) => {
					throw new FetchError(status, str);
				});
			}

			return null;
		}),
	);

const COMMON_AGG_REQUEST_CONFIG: {
	credentials: RequestCredentials;
	method: string;
	headers: Record<string, any>;
} = {
	credentials: 'include',
	method: 'POST',
	headers: {
		'Content-Type': 'application/json',
		'X-ExperimentalApi': 'ariGraph,devOps',
	},
};

type GraphQLErrors = Array<{
	message: string;
}>;

const throwGraphQLErrors = (errors: GraphQLErrors) => {
	const errStr = errors.map((err) => err.message).join(', ');
	throw new Error(errStr);
};

const handleFetchResponse = (response: Response) => {
	if (!response.ok) {
		const { status } = response;
		return response.text().then((str) => {
			throw new FetchError(status, str);
		});
	}

	return response.json();
};

const buildAris = ({
	cloudId,
	projectId,
	linkedPageId,
	spaceId,
}: ProjectSpaceLinkContext): { projectAri: string; pageOrSpaceAri: string } => {
	const projectAri = `ari:cloud:jira:${cloudId}:project/${projectId}`;
	// Construct the Page ARI if there is a linkedPageId otherwise construct the Space ARI
	const pageOrSpaceAri = linkedPageId
		? `ari:cloud:confluence:${cloudId}:page/${linkedPageId}`
		: `ari:cloud:confluence:${cloudId}:space/${spaceId}`;

	return { projectAri, pageOrSpaceAri };
};

type AGSRelationshipsResponse = {
	success: boolean;
	errors: GraphQLErrors;
};

// TODO remove pageId after productionisation -- needs a change in xflow to remove it as well
interface ProjectSpaceLinkContext {
	cloudId: string;
	linkedPageId: string | null;
	projectId: number;
	spaceId: number;
}

const CREATE_CONFLUENCE_RELATIONSHIP_QUERY = `
  mutation createConfluenceRelationship($fromAri: ID!, $toAri: ID!) {
    devOps {
      ariGraph {
        createRelationships(
          input: {relationships: {type: "project-documentation-entity", from: $fromAri, to: $toAri}}
        ) {
          success
          errors {
            message
          }
        }
      }
    }
  }
`;

type CreateRelationshipResponse = {
	devOps: {
		ariGraph: {
			createRelationships: AGSRelationshipsResponse;
		};
	};
};

export const createProjectSpaceLink$ = ({
	cloudId,
	linkedPageId,
	projectId,
	spaceId,
}: ProjectSpaceLinkContext) => {
	const { projectAri, pageOrSpaceAri } = buildAris({
		cloudId,
		linkedPageId,
		projectId,
		spaceId,
	});

	return fromPromise(
		fetch(getAggBaseUrl(), {
			...COMMON_AGG_REQUEST_CONFIG,
			body: JSON.stringify({
				query: CREATE_CONFLUENCE_RELATIONSHIP_QUERY,
				variables: { fromAri: projectAri, toAri: pageOrSpaceAri },
			}),
		})
			.then(handleFetchResponse)
			.then(({ data }: { data: CreateRelationshipResponse }) => {
				const { success, errors } = data?.devOps?.ariGraph?.createRelationships;
				if (!success) {
					throwGraphQLErrors(errors || []);
				}

				return null;
			}),
	);
};

const DELETE_CONFLUENCE_RELATIONSHIP_QUERY = `
  mutation deleteConfluenceRelationship($fromAri: ID!, $toAri: ID!) {
    devOps {
      ariGraph {
        deleteRelationships(
          input: {type: "project-documentation-entity", from: $fromAri, to: $toAri}
        ) {
          success
          errors {
            message
          }
        }
      }
    }
  }
`;

type DeleteRelationshipResponse = {
	devOps: {
		ariGraph: {
			deleteRelationships: AGSRelationshipsResponse;
		};
	};
};

export const deleteProjectSpaceLink$ = ({
	cloudId,
	linkedPageId,
	projectId,
	spaceId,
}: ProjectSpaceLinkContext) => {
	const { projectAri, pageOrSpaceAri } = buildAris({
		cloudId,
		linkedPageId,
		projectId,
		spaceId,
	});

	return fromPromise(
		fetch(getAggBaseUrl(), {
			...COMMON_AGG_REQUEST_CONFIG,
			body: JSON.stringify({
				query: DELETE_CONFLUENCE_RELATIONSHIP_QUERY,
				variables: { fromAri: projectAri, toAri: pageOrSpaceAri },
			}),
		})
			.then(handleFetchResponse)
			.then(({ data }: { data: DeleteRelationshipResponse }) => {
				const { success, errors } = data?.devOps?.ariGraph?.deleteRelationships;
				if (!success) {
					throwGraphQLErrors(errors || []);
				}

				return null;
			}),
	);
};

const GET_CONFLUENCE_RELATIONSHIP_QUERY = `
  query getConfluenceRelationship($projectAri: ID!) {
    devOps {
      ariGraph {
        relationships(
          filter: {
            from: $projectAri
            type: "project-documentation-entity"
          }
          sort: {
            lastUpdatedSortDirection: DESC
          }
        ) {
          nodes {
            to {
              id
              data {
                __typename
                ... on ConfluenceSpace {
                  key
                }
                ... on ConfluencePage {
                  pageId
                  space {
                    key
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  `;

type ConfluenceSpaceData = {
	data: {
		__typename: 'ConfluenceSpace';
		key: string;
	};
};

type ConfluencePageData = {
	data: {
		__typename: 'ConfluencePage';
		pageId: string;
		space: {
			key: string;
		};
	};
};

type GetConfluenceSpaceRelationship = {
	devOps: {
		ariGraph: {
			relationships: {
				nodes: Array<{
					lastUpdated: string;
					type: string;
					to: ConfluenceSpaceData | ConfluencePageData;
				}>;
			};
		};
	};
};

export const fetchProjectSpaceLink$ = (cloudId: string, projectId: number) => {
	const projectAri = `ari:cloud:jira:${cloudId}:project/${projectId}`;
	return fetchJson$<{ data: GetConfluenceSpaceRelationship }>(getAggBaseUrl(), {
		credentials: 'include',
		method: 'POST',
		headers: {
			'X-ExperimentalApi': 'ariGraph,devOps',
		},
		body: JSON.stringify({
			query: GET_CONFLUENCE_RELATIONSHIP_QUERY,
			variables: { projectAri },
		}),
	}).map(({ data }) => {
		const spacesAndPages = data.devOps.ariGraph.relationships.nodes.filter((node) =>
			['ConfluencePage', 'ConfluenceSpace'].includes(node.to.data.__typename),
		);
		// TODO: Project pages should implement a more robust solution here rather than reading the first in the list of returned relationships, because there could be many associated pages/spaces
		const [relationshipNode] = spacesAndPages;

		if (relationshipNode == null) {
			return {
				spaceKey: null,
				linkedPageId: null,
			};
		}
		const spaceKey =
			relationshipNode.to.data.__typename === 'ConfluencePage'
				? relationshipNode.to.data.space.key
				: relationshipNode.to.data.key;
		const linkedPageId =
			relationshipNode.to.data.__typename === 'ConfluencePage'
				? relationshipNode.to.data.pageId
				: null;

		return { spaceKey, linkedPageId };
	});
};

export const fetchConnectedSpaceBlueprints$ = (
	spaceKey: string,
	projectId?: number,
	projectType?: string,
) =>
	fetchJson$(getBlueprintsUrl(spaceKey), {
		credentials: 'include',
		headers: {
			[IGNORE_MAU_HEADER]: 'true',
			[EP_ENITITY_ID_HEADER]: JSON.stringify({ projectId, projectType }),
		},
	});

export const fetchConfluenceCurrentUser = (projectId?: number, projectType?: string) =>
	fetchJson$(getConfluenceCurrentUserUrl(), {
		credentials: 'include',
		headers: {
			[IGNORE_MAU_HEADER]: 'true',
			[EP_ENITITY_ID_HEADER]: JSON.stringify({ projectId, projectType }),
		},
	});

const sanitizeInput = (input = '') => input.replace(/"|\*|~/g, '\\$&');

export const searchConfluencePages = (
	searchString?: string,
	projectId?: number,
	projectType?: string,
) => {
	let cqlQuery: string;
	if (searchString) {
		cqlQuery = `type=page and title ~ "${sanitizeInput(searchString)}"`;
	} else {
		cqlQuery = `type=page`;
	}

	return fetchJson$<ConfluenceSearchResultWithLinks>(getConfluencePageSearchURL(cqlQuery), {
		credentials: 'include',
		headers: {
			'Content-Type': 'application/json',
			[IGNORE_MAU_HEADER]: 'true',
			[EP_ENITITY_ID_HEADER]: JSON.stringify({ projectId, projectType }),
		},
	}).toPromise();
};

interface CrossProductSearchSpaceItem {
	title: string;
	baseUrl: string;
	id: string;
	space: {
		key: string;
		icon: {
			path: string;
		};
	};
	url: 'string';
}

export interface CrossProductSearchSpaceResult {
	scopes: [
		{
			id: 'confluence.space';
			results: CrossProductSearchSpaceItem[];
			size: number;
		},
	];
}

export const searchConfluenceSpaces = ({
	searchString,
	cloudId,
	limit,
}: {
	searchString?: string;
	cloudId: string;
	limit: number;
}) => {
	const query = searchString ? sanitizeInput(searchString) : '';
	return fetchJson$<CrossProductSearchSpaceResult>(CONFLUENCE_SPACE_SEARCH_URL, {
		method: 'POST',
		credentials: 'include',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({
			query,
			cloudId,
			limit,
			scopes: ['confluence.space'],
			experience: 'jira.projectPages',
		}),
	}).toPromise();
};

export const getConfluenceAccessRequestCapabilities$ = (cloudId: string) =>
	fetchJson$<AccessRequestCapabilityResponse>(getConfluenceAccessCapabilitiesUrl(cloudId), {
		credentials: 'include',
	});

export const getConfluencePageContent$ = (
	pageId: string,
	projectId?: number,
	projectType?: string,
) =>
	fetchJson$<ConfluencePage>(getConfluencePageUrl(pageId), {
		credentials: 'include',
		headers: {
			[EP_ENITITY_ID_HEADER]: JSON.stringify({ projectId, projectType }),
		},
	});

export const getConfluenceSpace$ = (spaceKey: string, projectId?: number, projectType?: string) =>
	fetchJson$<ConfluenceSpaceExpanded>(`/wiki/rest/api/space/${spaceKey}?expand=icon,homepage`, {
		credentials: 'include',
		headers: {
			[EP_ENITITY_ID_HEADER]: JSON.stringify({ projectId, projectType }),
		},
	});

interface JiraProjectPermission {
	id: string;
	name: string;
	description: string;
	havePermission: boolean;
}

export enum JiraProjectPermissionType {
	EditIssues = 'EDIT_ISSUES',
	CreateIssues = 'CREATE_ISSUES',
}

export interface JiraProjectPermissionsResponse {
	permissions: {
		[JiraProjectPermissionType.CreateIssues]: JiraProjectPermission;
		[JiraProjectPermissionType.EditIssues]: JiraProjectPermission;
	};
}

export const getJiraProjectPermissions$ = (projectKey: string) =>
	fetchJson$<JiraProjectPermissionsResponse>(getJiraProjectPermissionsUrl(projectKey), {
		credentials: 'include',
	});

export const requestPersonalizationSiteTraits$ = (cloudId?: string) =>
	fetchJson$(getPersonalizationSiteTraitsUrl(cloudId), {
		method: 'GET',
		credentials: 'include',
		headers: {
			'Content-Type': 'application/json',
			[IGNORE_MAU_HEADER]: 'true',
		},
	}).map((response) => response.attributes);

// CONFLUENCE ON DEMAND ACTIVATION
type ProjectContext = {
	projectId: string | number | undefined;
};

export const TaskName = [
	'activate',
	'wait-for-activation',
	'wait-for-confluence',
	'create-space',
	'link-space',
] as const;

export type TaskName = (typeof TaskName)[number];
export const ProvisioningStatus = [
	'PROVISIONING_IN_PROGRESS',
	'PROVISIONING_COMPLETED',
	'PROVISIONING_FAILED',
	'PROVISIONING_PROCESS_NOT_FOUND',
] as const;

export type ProvisioningStatus = (typeof ProvisioningStatus)[number];

export interface ProvisioningApiStartResponse {
	started: boolean;
}
export const startConfluenceActivation$ = (cloudId: string, body: ProjectContext) =>
	fetchJson$<ProvisioningApiStartResponse>(`${confluenceActivationBaseUrl(cloudId)}/start`, {
		method: 'POST',
		credentials: 'include',
		headers: {
			'Content-Type': 'application/json',
			[IGNORE_MAU_HEADER]: 'true',
		},
		body: JSON.stringify({
			...body,
			sourceContext: PROJECT_PAGES_SOURCE_CONTEXT,
			sourceComponent: PROJECT_PAGES_SOURCE_COMPONENT,
		}),
	});
export interface ProvisioningApiPlanResponse {
	allowed: boolean;
	tasks: {
		name: TaskName;
		allowed: boolean;
		context?: {
			canActivate: boolean;
			canUserAddProduct: boolean;
		};
	}[];
	context?: {
		status: ProvisioningStatus;
	};
}
export const planConfluenceActivation$ = (cloudId: string, body: ProjectContext) =>
	fetchJson$<ProvisioningApiPlanResponse>(`${confluenceActivationBaseUrl(cloudId)}/plan`, {
		method: 'POST',
		credentials: 'include',
		headers: {
			'Content-Type': 'application/json',
			[IGNORE_MAU_HEADER]: 'true',
		},
		body: JSON.stringify({
			...body,
			sourceContext: PROJECT_PAGES_SOURCE_CONTEXT,
			sourceComponent: PROJECT_PAGES_SOURCE_COMPONENT,
		}),
	});
export interface ProvisioningApiStatusResponse {
	status: ProvisioningStatus;
}
export const checkConfluenceActivationStatus$ = (cloudId: string) =>
	fetchJson$<ProvisioningApiStatusResponse>(`${confluenceActivationBaseUrl(cloudId)}/status`, {
		method: 'GET',
		credentials: 'include',
	});
